import { useState, useEffect, useMemo } from "react";
import axiosInstance from "../../../axiosConfig";
import FileInput from "../UI/InputFile";

export default function PricingOwnership({
  ticketDetails,
  setTicketDetails,
  handleSubmit,
  handleOwnershipChange,
  handleTicketImagesChange,
  addOwnershipItem,
  removeOwnershipItem,
}) {
  const [metaData, setMetaData] = useState({
    ownershipTypes: [],
    ticketOrigins: [],
    ticketAgeGroups: [],
  });
  const [loading, setLoading] = useState(true);

  const [destinationOwnerships, setDestinationOwnerships] = useState([]);
  const [isLoadingOwnerships, setIsLoadingOwnerships] = useState(false);

  // -----------------------------
  // Fetch ticket meta
  // -----------------------------
  useEffect(() => {
    const fetchMetaData = async () => {
      try {
        const response = await axiosInstance.get(
          "/internal-api/v1/client-web/destination-panel/ticket/meta"
        );

        if (response.data?.result) {
          setMetaData(response.data.result);
        }
      } catch (error) {
        console.error("Error fetching metadata:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchMetaData();
  }, []);

  // -----------------------------
  // Fetch destination ownerships
  // -----------------------------
  useEffect(() => {
    const fetchDestinationOwnerships = async () => {
      if (!ticketDetails.destinationId) {
        setDestinationOwnerships([]);

        // Clear selected ownership IDs if destination changes
        if (ticketDetails.ownership?.length) {
          setTicketDetails((prev) => ({
            ...prev,
            ownership: prev.ownership.map((item) => ({ ...item, id: "" })),
          }));
        }
        return;
      }

      try {
        setIsLoadingOwnerships(true);
        const response = await axiosInstance.post(
          "/internal-api/v1/client-web/destination-panel/config/ownerships",
          { destinationIds: [Number(ticketDetails.destinationId)] }
        );

        const nextOwnerships = response.data?.result || response.data?.data || [];
        const normalized = Array.isArray(nextOwnerships) ? nextOwnerships : [];
        setDestinationOwnerships(normalized);

        // Validate existing selected ownership IDs
        if (ticketDetails.ownership?.length) {
          const allowedIds = new Set(normalized.map((item) => String(item.id)));
          const hasInvalid = ticketDetails.ownership.some(
            (item) => item.id && !allowedIds.has(String(item.id))
          );

          if (hasInvalid) {
            setTicketDetails((prev) => ({
              ...prev,
              ownership: prev.ownership.map((item) =>
                item.id && !allowedIds.has(String(item.id)) ? { ...item, id: "" } : item
              ),
            }));
          }
        }
      } catch (error) {
        console.error("Error fetching destination ownerships:", error);
        setDestinationOwnerships([]);
      } finally {
        setIsLoadingOwnerships(false);
      }
    };

    fetchDestinationOwnerships();
  }, [ticketDetails.destinationId, setTicketDetails]);

  // -----------------------------
  // Build image-variant combos (Origin x AgeGroup)
  // (for Ticket Images section only)
  // -----------------------------
  const imageCombinations = useMemo(() => {
    const combos = [];
    const selectedOrigins = ticketDetails.ticketOrigins || [];
    const selectedAgeGroups = ticketDetails.ticketAgerId || [];

    if (!selectedOrigins.length || !selectedAgeGroups.length) return [];

    selectedOrigins.forEach((originId) => {
      const origin = metaData.ticketOrigins?.find((o) => String(o.id) === String(originId));

      selectedAgeGroups.forEach((ageGroupId) => {
        const age = metaData.ticketAgeGroups?.find(
          (ag) => String(ag.id) === String(ageGroupId)
        );

        if (origin && age) {
          combos.push({
            originId,
            originName: origin.name,
            ageGroupId,
            ageGroupName: age.name,
            key: `${originId}_${ageGroupId}`,
            tabLabel: `${origin.name} - ${age.name}`,
          });
        }
      });
    });

    return combos;
  }, [ticketDetails.ticketOrigins, ticketDetails.ticketAgerId, metaData.ticketOrigins, metaData.ticketAgeGroups]);

  const totalImageTabs = imageCombinations.length || 1;

  // -----------------------------
  // Ensure ownership array exists
  // -----------------------------
  useEffect(() => {
    if (!ticketDetails.ownership || ticketDetails.ownership.length === 0) {
      setTicketDetails((prev) => ({
        ...prev,
        ownership: [{ id: "", share: "" }],
      }));
    }
  }, [ticketDetails.ownership, setTicketDetails]);

  // -----------------------------
  // Ensure ticketImages structure matches variant combos
  // -----------------------------
  useEffect(() => {
    if (!imageCombinations.length) return;

    const currentTicketImages = Array.isArray(ticketDetails.ticketImages)
      ? ticketDetails.ticketImages
      : [];
    const needsInit =
      currentTicketImages.length !== totalImageTabs ||
      imageCombinations.some((combo, index) => {
        const imageGroup = currentTicketImages[index];
        return (
          String(imageGroup?.originId) !== String(combo.originId) ||
          String(imageGroup?.ageGroupId) !== String(combo.ageGroupId) ||
          !Array.isArray(imageGroup?.images)
        );
      });

    if (needsInit) {
      const next = imageCombinations.map((c) => ({
        originId: c.originId,
        ageGroupId: c.ageGroupId,
        images:
          currentTicketImages.find(
            (imageGroup) =>
              String(imageGroup?.originId) === String(c.originId) &&
              String(imageGroup?.ageGroupId) === String(c.ageGroupId)
          )?.images || [],
      }));

      setTicketDetails((prev) => ({
        ...prev,
        ticketImages: next,
      }));
    }
  }, [imageCombinations, totalImageTabs, ticketDetails.ticketImages, setTicketDetails]);

  // -----------------------------
  // Ownership helpers
  // -----------------------------
  const ownershipEntries = Array.isArray(ticketDetails.ownership) ? ticketDetails.ownership : [];
  const totalShare = ownershipEntries.reduce(
    (sum, entry) => sum + (parseFloat(entry.share) || 0),
    0
  );

  const handleOwnershipFieldChange = (index, field, value) => {
    if (!ownershipEntries[index]) return;
    handleOwnershipChange(index, field, value);
  };

  // -----------------------------
  // Ticket Images helpers
  // -----------------------------
  const [activeImagesTab, setActiveImagesTab] = useState(0);
  const today = new Date().toISOString().split("T")[0];

  const createEmptyImage = () => ({
    mobileImageFile: null,
    imageFile: null,
    displayOrder: "1",
    aspDivHeight: "100",
    aspDivWidth: "100",
    validFrom: today,
    validTo: today,
  });

  const currentTab = ticketDetails.ticketImages?.[activeImagesTab];
  const currentImages = Array.isArray(currentTab?.images) ? currentTab.images : [];

  const updateImagesForActiveTab = (nextImages) => {
    handleTicketImagesChange(activeImagesTab, "images", nextImages);
  };

  const handleTicketImageChange = (imageIndex, field, value) => {
    const next = currentImages.map((img, idx) =>
      idx === imageIndex ? { ...img, [field]: value } : img
    );
    updateImagesForActiveTab(next);
  };

  const removeTicketImage = (imageIndex) => {
    const next = currentImages.filter((_, idx) => idx !== imageIndex);
    updateImagesForActiveTab(next);
  };

  const addImage = () => {
    updateImagesForActiveTab([...currentImages, createEmptyImage()]);
  };

  if (loading) {
    return (
      <div className="min-h-screen p-4 text-sm bg-slate-50">
        <div className="max-w-5xl mx-auto">
          <div className="p-6 bg-white rounded-md shadow">
            <div className="text-center">Loading metadata...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-4 text-sm bg-slate-50">
      <div className="max-w-5xl mx-auto space-y-6">
        {/* ---------------- Ownership ---------------- */}
        <div className="p-6 bg-white rounded-md shadow">
          <h2 className="mb-4 text-base font-medium text-slate-700">Ownership</h2>

          <div className="p-4 mb-4 rounded-md bg-slate-50">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-medium text-slate-700">Ownership Configuration</h3>
              <span
                className={`text-xs ${
                  Math.abs(totalShare - 100) < 0.01 ? "text-green-600" : "text-red-600"
                }`}
              >
                Total share: {totalShare.toFixed(2)}%
              </span>
            </div>

            <div className="space-y-4">
              {ownershipEntries.map((entry, index) => (
                <div key={index} className="grid grid-cols-1 gap-4 md:grid-cols-3">
                  <div className="md:col-span-2">
                    <label className="block mb-1 text-xs font-medium text-slate-700">
                      Share Ownership <span className="text-red-500">*</span>
                    </label>

                    <select
                      value={entry.id || ""}
                      onChange={(e) => handleOwnershipFieldChange(index, "id", e.target.value)}
                      className="block w-full py-2 pl-3 pr-10 text-xs border rounded-md border-slate-300 focus:outline-none focus:ring-2 focus:ring-brand-blue/30"
                      disabled={!ticketDetails.destinationId || isLoadingOwnerships}
                    >
                      <option value="">
                        {isLoadingOwnerships ? "Loading ownerships..." : "Select merchant"}
                      </option>
                      {destinationOwnerships.map((o) => (
                        <option key={o.id} value={o.id}>
                          {o.ebName || o.eName || `Ownership ${o.id}`}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block mb-1 text-xs font-medium text-slate-700">
                      Share Percentage <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      value={entry.share || ""}
                      onChange={(e) => handleOwnershipFieldChange(index, "share", e.target.value)}
                      placeholder="0.00 %"
                      className="block w-full p-2 text-xs border rounded-md border-slate-300 focus:outline-none focus:ring-2 focus:ring-brand-blue/30"
                    />
                  </div>

                  {ownershipEntries.length > 1 && (
                    <div className="flex justify-end md:col-span-3">
                      <button
                        type="button"
                        onClick={() => removeOwnershipItem?.(index)}
                        className="px-4 py-2 text-xs text-white bg-red-500 rounded hover:bg-red-600"
                      >
                        Remove
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="flex justify-end mt-4">
              <button
                type="button"
                onClick={() => addOwnershipItem?.()}
                className="px-4 py-2 text-xs text-white bg-brand-blue rounded hover:bg-brand-blue-dark"
              >
                Add Ownership
              </button>
            </div>
          </div>
        </div>

        {/* ---------------- Ticket Images ---------------- */}
        <div className="p-6 bg-white rounded-md shadow">
          <h2 className="mb-4 text-base font-medium text-slate-700">Ticket Images</h2>

          {imageCombinations.length === 0 ? (
            <div className="p-4 mb-4 border border-yellow-200 rounded-md bg-yellow-50">
              <p className="text-sm text-yellow-800">
                Please select ticket origins and age groups to configure ticket images.
              </p>
            </div>
          ) : (
            <>
              {/* Tabs */}
              <div className="flex flex-wrap mb-6 overflow-x-auto border-b border-slate-200">
                {imageCombinations.map((combination, index) => (
                  <button
                    key={combination.key}
                    type="button"
                    onClick={() => setActiveImagesTab(index)}
                    className={`px-4 py-2 text-xs font-medium border-b-2 mr-2 mb-2 whitespace-nowrap transition-colors flex-shrink-0 ${
                      activeImagesTab === index
                        ? "border-purple-500 text-purple-600 bg-purple-50"
                        : "border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300"
                    }`}
                  >
                    {combination.tabLabel}
                  </button>
                ))}
              </div>

              <div className="space-y-4">
                <h3 className="text-sm font-medium text-slate-700">
                  Images Configuration - {imageCombinations[activeImagesTab]?.tabLabel}
                </h3>

                {currentImages.length > 0 &&
                  currentImages.map((image, idx) => (
                    <div key={idx} className="p-4 mb-4 rounded-md bg-slate-50">
                      <h4 className="mb-3 text-sm font-medium text-slate-700">Image {idx + 1}</h4>

                      <div className="grid grid-cols-3 gap-4 mb-4">
                        <div>
                          <label className="block mb-1 text-xs font-medium text-slate-700">
                            Display Order
                          </label>
                          <input
                            type="number"
                            value={image.displayOrder}
                            onChange={(e) =>
                              handleTicketImageChange(idx, "displayOrder", e.target.value)
                            }
                            min="1"
                            className="block w-full p-2 text-xs border rounded-md border-slate-300 focus:outline-none focus:ring-2 focus:ring-brand-blue/30"
                          />
                        </div>

                        <div>
                          <label className="block mb-1 text-xs font-medium text-slate-700">
                            Valid From
                          </label>
                          <input
                            type="date"
                            value={image.validFrom}
                            onChange={(e) => handleTicketImageChange(idx, "validFrom", e.target.value)}
                            className="block w-full p-2 text-xs border rounded-md border-slate-300 focus:outline-none focus:ring-2 focus:ring-brand-blue/30"
                          />
                        </div>

                        <div>
                          <label className="block mb-1 text-xs font-medium text-slate-700">
                            Valid To
                          </label>
                          <input
                            type="date"
                            value={image.validTo}
                            onChange={(e) => handleTicketImageChange(idx, "validTo", e.target.value)}
                            className="block w-full p-2 text-xs border rounded-md border-slate-300 focus:outline-none focus:ring-2 focus:ring-brand-blue/30"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 gap-4 mb-4 md:grid-cols-2">
                        <FileInput
                          value={ticketDetails.ticketImages?.[activeImagesTab]?.images?.[idx]?.imageFile}
                          onChange={(file) => handleTicketImageChange(idx, "imageFile", file)}
                          label="Upload"
                          title="Web image"
                        />

                        <FileInput
                          value={
                            ticketDetails.ticketImages?.[activeImagesTab]?.images?.[idx]?.mobileImageFile
                          }
                          onChange={(file) => handleTicketImageChange(idx, "mobileImageFile", file)}
                          label="Upload"
                          title="Mobile image"
                        />
                      </div>

                      <div className="flex justify-end">
                        <button
                          type="button"
                          onClick={() => removeTicketImage(idx)}
                          className="px-4 py-2 text-xs text-white bg-red-500 rounded hover:bg-red-600"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  ))}

                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={addImage}
                    className="px-4 py-2 text-xs text-white bg-brand-blue rounded hover:bg-brand-blue-dark"
                  >
                    Add Image
                  </button>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Submit */}
        <div className="flex-center">
          <button
            type="button"
            onClick={handleSubmit}
            className="px-6 py-3 text-xs font-medium text-white bg-brand-blue rounded hover:bg-brand-blue-dark"
          >
            Submit Ticket
          </button>
        </div>
      </div>
    </div>
  );
}
